import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

interface Account {
  _id?: string;
  id?: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
  createdAt: string;
}

/**
 * The accounts this console operates on behalf of.
 *
 * Worth being plain about what this screen does and does not mean: an account
 * here is a name, not a boundary. Nothing in the backend is scoped by account
 * yet, so a second account added on this screen would see the first one's
 * orders, products and posts. The notice below says so on the screen itself,
 * because an operator who creates an account and starts using it is relying
 * on an isolation that does not exist.
 */
const AccountsSection: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getAccounts() as unknown as { accounts: Account[] };
      setAccounts(res.accounts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Shown under the name field so the handle is visible before it is fixed.
  // The server derives the same value; this is a preview, not the source.
  const previewSlug = name
    .trim()
    .toLowerCase()
    .replaceAll('&', ' and ')
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
    .slice(0, 60);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || creating) return;

    setCreating(true);
    setCreateError(null);
    try {
      await apiService.createAccount(name.trim(), slug.trim() || undefined);
      setName('');
      setSlug('');
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create the account.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Not a styling flourish. Someone adding a second brand here will
          assume it is separated from the first, and it is not. */}
      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4">
        <p className="font-body text-sm text-amber-900">
          <span className="font-bold">Accounts are names, not boundaries.</span>{' '}
          Nothing is scoped by account yet — orders, products, posts and media
          are shared across every account in this list. Adding a second account
          does not separate its data from the first.
        </p>
      </div>

      <form
        onSubmit={handleCreate}
        className="rounded-2xl border-2 border-ice-teal-light bg-white p-6 space-y-4"
      >
        <h2 className="font-title text-lg font-bold text-deep-navy">Add an account</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body text-xs font-bold text-gray-600">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Waddle &amp; Sip"
              className="w-full rounded-xl border-2 border-ice-teal-light px-3 py-2 font-body text-sm"
            />
            {name.trim() && !slug.trim() && (
              <span className="mt-1 block font-body text-xs text-gray-500">
                Handle: <code>{previewSlug || '—'}</code>
              </span>
            )}
          </label>

          <label className="block">
            <span className="mb-1 block font-body text-xs font-bold text-gray-600">
              Handle <span className="font-normal">(optional)</span>
            </span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="derived from the name"
              className="w-full rounded-xl border-2 border-ice-teal-light px-3 py-2 font-body text-sm"
            />
            <span className="mt-1 block font-body text-xs text-gray-500">
              Fixed once created — other records will key on it.
            </span>
          </label>
        </div>

        {createError && (
          <p className="font-body text-sm text-red-700">{createError}</p>
        )}

        <button
          type="submit"
          disabled={!name.trim() || creating}
          className="rounded-xl bg-brand-blue px-4 py-2.5 font-bold text-white transition-colors hover:bg-brand-blue-dark disabled:opacity-50"
        >
          {creating ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <div className="rounded-2xl border-2 border-ice-teal-light bg-white p-6">
        <h2 className="mb-4 font-title text-lg font-bold text-deep-navy">
          Accounts {!loading && <span className="font-body text-sm font-normal text-gray-500">({accounts.length})</span>}
        </h2>

        {loading && <p className="font-body text-sm text-gray-600">Loading…</p>}

        {error && (
          <div className="font-body text-sm text-red-700">
            {error}{' '}
            <button onClick={() => void load()} className="underline">Retry</button>
          </div>
        )}

        {!loading && !error && accounts.length === 0 && (
          <p className="font-body text-sm text-gray-600">
            No accounts yet. The first one names the brand this data already belongs to.
          </p>
        )}

        {!loading && !error && accounts.length > 0 && (
          <ul className="divide-y divide-ice-teal-light">
            {accounts.map((account) => (
              <li key={account._id || account.id || account.slug} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-body text-sm font-bold text-deep-navy">{account.name}</p>
                  <p className="font-body text-xs text-gray-500">
                    <code>{account.slug}</code>
                    {account.createdAt && ` · added ${new Date(account.createdAt).toLocaleDateString()}`}
                  </p>
                </div>
                <span
                  className={`rounded px-2 py-0.5 font-body text-xs font-bold ${
                    account.status === 'active'
                      ? 'bg-ice-teal-light text-deep-navy'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {account.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AccountsSection;
